import { useEffect, useRef, useState, useCallback } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Network } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { captureError } from '@/lib/sentry';

interface Node {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  group?: string;
  val?: number;
  x?: number;
  y?: number;
}

interface Link {
  source: string;
  target: string;
  type: string;
}

interface GraphData {
  nodes: Node[];
  links: Link[];
}

interface ConnectionType {
  type: string;
  enabled: boolean;
}

export function VisualizationPage() {
  const graphRef = useRef<HTMLDivElement>(null);
  const graphInstanceRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [graphData, setGraphData] = useState<GraphData>({ nodes: [], links: [] });
  const { currentWorkspace } = useWorkspaces();
  const { toast } = useToast();
  const [connectionTypes, setConnectionTypes] = useState<ConnectionType[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!currentWorkspace) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch contacts
        const { data: contacts, error: contactsError } = await supabase
          .from('contacts')
          .select('id, first_name, last_name, company_name')
          .eq('workspace_id', currentWorkspace.id);

        if (contactsError) throw contactsError;

        // Fetch connections
        const { data: connections, error: connectionsError } = await supabase
          .from('connections_by_workspace')
          .select('contact_id_1, contact_id_2, relationship_type')
          .eq('workspace_id', currentWorkspace.id);

        if (connectionsError) throw connectionsError;

        // Create nodes
        const nodes = contacts.map(contact => ({
          id: contact.id,
          name: `${contact.first_name} ${contact.last_name}`,
          firstName: contact.first_name,
          lastName: contact.last_name,
          group: contact.company_name || 'No Company',
          val: 1
        }));

        // Create links and track unique connection types
        const linkSet = new Set<string>();
        const links: Link[] = [];
        const uniqueTypes = new Set<string>();

        connections?.forEach(conn => {
          const pairKey1 = `${conn.contact_id_1}-${conn.contact_id_2}`;
          const pairKey2 = `${conn.contact_id_2}-${conn.contact_id_1}`;

          if (!linkSet.has(pairKey1) && !linkSet.has(pairKey2)) {
            links.push({
              source: conn.contact_id_1,
              target: conn.contact_id_2,
              type: conn.relationship_type
            });
            links.push({
              source: conn.contact_id_2,
              target: conn.contact_id_1,
              type: conn.relationship_type
            });
            linkSet.add(pairKey1);
            uniqueTypes.add(conn.relationship_type);
          }
        });

        // Initialize connection types with all enabled
        setConnectionTypes(Array.from(uniqueTypes).map(type => ({
          type,
          enabled: true
        })));

        setGraphData({ nodes, links });
      } catch (error) {
        console.error('Error fetching graph data:', error);
        setError('Failed to load visualization data');
        captureError(error as Error, { feature: 'visualization-fetch-data' });
        toast({
          title: "Error",
          description: "Failed to load visualization data",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentWorkspace?.id]);

  const toggleConnectionType = (type: string) => {
    setConnectionTypes(prev => prev.map(ct => 
      ct.type === type ? { ...ct, enabled: !ct.enabled } : ct
    ));
  };

  // Filter links based on enabled connection types
  const filteredLinks = graphData.links.filter(link =>
    connectionTypes.find(ct => ct.type === link.type)?.enabled
  );

  const handleNodeCanvasObject = useCallback((node: Node, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const label = `${node.firstName} ${node.lastName}`;
    const fontSize = 12 / globalScale;
    ctx.font = `${fontSize}px Sans-Serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#666666';

    if (globalScale > 1) {
      const xOffset = 10 / globalScale;
      const yOffset = 0;
      ctx.fillText(label, node.x! + xOffset, node.y! + yOffset);
    }
  }, []);

  // handleD3Force function removed as it was unused

  if (!currentWorkspace) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-3.5rem)]">
        <div className="text-center text-muted-foreground">
          No workspace selected
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-3.5rem)]">
        <div className="text-center space-y-4 p-8 rounded-lg bg-background/95 backdrop-blur">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" />
          <p className="text-muted-foreground">Loading contact data...</p>
          <p className="text-xs text-muted-foreground">This may take a moment</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-3.5rem)]">
        <div className="text-center space-y-4">
          <p className="text-destructive">{error}</p>
          <Button onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (graphData.nodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-3.5rem)]">
        <div className="text-center space-y-4">
          <Network className="h-12 w-12 mx-auto text-muted-foreground" />
          <p className="text-muted-foreground">No contacts found in this workspace.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-3.5rem)] flex">
      {/* Sidebar */}
      <div className="w-64 border-r bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="p-4 border-b">
          <h2 className="font-semibold">Connection Types</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {graphData.nodes.length} contacts, {filteredLinks.length} connections
          </p>
        </div>
        <ScrollArea className="h-[calc(100%-5rem)] p-4">
          <div className="space-y-4">
            {connectionTypes.map(({ type, enabled }) => (
              <div key={type} className="flex items-center space-x-2">
                <Checkbox
                  id={type}
                  checked={enabled}
                  onCheckedChange={() => toggleConnectionType(type)}
                />
                <Label htmlFor={type} className="capitalize">
                  {type}
                </Label>
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Graph */}
      <div className="flex-1 relative" ref={graphRef}>
        <ForceGraph2D
          ref={graphInstanceRef}
          graphData={{ nodes: graphData.nodes, links: filteredLinks }}
          nodeLabel="name"
          nodeCanvasObject={handleNodeCanvasObject}
          nodeCanvasObjectMode={() => 'after'}
          nodeColor={() => '#000000'}
          linkColor={() => '#cccccc'}
          backgroundColor="#ffffff"
          nodeRelSize={6}
          linkWidth={1}
          width={graphRef.current?.clientWidth || window.innerWidth - 256} // Subtract sidebar width
          height={graphRef.current?.clientHeight || window.innerHeight - 56}

          onEngineStop={() => {
            if (graphInstanceRef.current && filteredLinks.length === 0) {
              graphInstanceRef.current.zoomToFit(400);
            }
          }}
        />
      </div>
    </div>
  );
}